import React, { useId, useImperativeHandle, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import MultiTextField from './MultiTextField';
import { durationUnitLabels } from '../translations';
import { dateFormat } from '../../../helpers/datetime_helpers';
import {
  buildRelativeDuration,
  defaultRelativeDuration,
  parseRelativeDuration,
  type RelativeDurationUnit,
} from '../../../helpers/filter_helpers';
import { Autocomplete } from '@/components/core/Autocomplete';
import { sanitizedParseFloat } from '@/helpers/math_helpers';
import { useEntityPropertyOptions } from '@/hooks/useEntityPropertyOptions';
import { Dropdown, DropdownHandle } from '@/components/core/Dropdown';
import Input from '@/components/core/Input';

const messages = defineMessages({
  specifyValue: {
    id: 'filter_condition_row.specify_value',
    defaultMessage: 'Specify value',
  },
  fromValue: {
    id: 'filter_condition_row.from_value',
    defaultMessage: 'From',
  },
  toValue: {
    id: 'filter_condition_row.to_value',
    defaultMessage: 'To',
  },
  and: {
    id: 'filter_condition_input.and',
    defaultMessage: 'and',
  },
  selectUnit: {
    id: 'filter_condition_input.select_unit',
    defaultMessage: 'Select unit',
  },
});

const RELATIVE_DURATION_UNITS: RelativeDurationUnit[] = [
  'days',
  'months',
  'years',
];

const MAX_VISIBLE_OPTIONS = 100;

const coerceNumberToInputValue = (probablyNumber: number): string => {
  return typeof probablyNumber === 'number' && isFinite(probablyNumber)
    ? probablyNumber.toString()
    : '';
};

/** Native date inputs only accept `yyyy-MM-dd`, which is what `dateFormat` is. */
const coerceDateToInputValue = (isoDate: string): string => {
  const date = DateTime.fromISO(isoDate);
  return date.isValid ? date.toFormat(dateFormat) : '';
};

export const defaultValue = (
  type: FilterCondition['type'],
  operator: FilterCondition['operator'],
  alternatives?: string[],
): unknown => {
  switch (operator) {
    case 'eq':
    case 'neq':
      if (alternatives && alternatives.length > 0) {
        return alternatives[0];
      }
      switch (type) {
        case 'string':
          return '';
        case 'number':
          return 0;
        default:
          return undefined;
      }
    case 'after':
    case 'before':
      return DateTime.local().toFormat(dateFormat);
    case 'in_last':
    case 'not_in_last':
      return buildRelativeDuration(defaultRelativeDuration);
    case 'false':
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
    case 'unknown':
    case 'true':
    case 'known':
    case 'matches':
    case 'not_matches':
      return undefined;
    case 'all_of':
    case 'any_of':
    case 'none_of':
      return [];
    case 'between':
    case 'not_between':
      if (type === 'date') {
        return [
          DateTime.local().toFormat(dateFormat),
          DateTime.local().toFormat(dateFormat),
        ];
      }
      return [];
    default:
      return undefined;
  }
};

const blurOnEnter: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
  if (event.key === 'Enter') {
    event.currentTarget.blur();
  }
};

const RangeInputs: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-row items-center gap-2">{children}</div>
);

const RangeSeparator: React.FC = () => (
  <p className="text-sm">
    <FormattedMessage {...messages.and} />
  </p>
);

export interface FilterConditionInputHandle {
  /**
   * Moves focus to the input and opens its list of options. Does nothing for
   * the operators whose input has no options to pick from.
   */
  focusOptions: () => void;
}

interface FilterConditionInputProps {
  filterCondition: FilterCondition;
  onChange: (filterCondition: FilterCondition) => void;
  schema: EntitySchema;
  /** The element the suggestion list is kept inside of. */
  containerRef?: React.RefObject<HTMLElement>;
}

const FilterConditionInput = React.forwardRef<
  FilterConditionInputHandle,
  FilterConditionInputProps
>(({ filterCondition, onChange, schema, containerRef }, ref) => {
  const intl = useIntl();
  const inputId = `filter-condition-value-${useId()}`;
  const schemaProp = useMemo(
    () => schema.definition.propertiesLookup[filterCondition.field],
    [filterCondition.field, schema.definition.propertiesLookup],
  );
  const valueOptions = useEntityPropertyOptions(schemaProp);

  // Only one of these is attached at a time, since the operator decides which
  // input the value is given through.
  const dropdownRef = useRef<DropdownHandle>(null);
  const suggestionRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focusOptions: () => {
        if (dropdownRef.current) {
          dropdownRef.current.open();
          return;
        }
        // A suggestion field opens its list on its own once it has focus.
        suggestionRef.current?.focus();
      },
    }),
    [],
  );

  const suggestionProps = {
    id: inputId,
    options: valueOptions,
    maxVisibleOptions: MAX_VISIBLE_OPTIONS,
    containerRef,
    placeholder: intl.formatMessage(messages.specifyValue),
  };

  if (filterCondition.type === 'date') {
    switch (filterCondition.operator) {
      case 'before':
      case 'after': {
        return (
          <Input
            id={inputId}
            type="date"
            aria-label={intl.formatMessage(messages.specifyValue)}
            value={coerceDateToInputValue(filterCondition.value)}
            onChange={(e) =>
              onChange({ ...filterCondition, value: e.target.value })
            }
            onKeyDown={blurOnEnter}
          />
        );
      }
      case 'between':
      case 'not_between': {
        return (
          <RangeInputs>
            <Input
              id={`${inputId}-from`}
              type="date"
              aria-label={intl.formatMessage(messages.fromValue)}
              value={coerceDateToInputValue(filterCondition.value[0])}
              onChange={(e) =>
                onChange({
                  ...filterCondition,
                  value: [e.target.value, filterCondition.value[1]],
                })
              }
              onKeyDown={blurOnEnter}
            />
            <RangeSeparator />
            <Input
              id={`${inputId}-to`}
              type="date"
              aria-label={intl.formatMessage(messages.toValue)}
              value={coerceDateToInputValue(filterCondition.value[1])}
              onChange={(e) =>
                onChange({
                  ...filterCondition,
                  value: [filterCondition.value[0], e.target.value],
                })
              }
              onKeyDown={blurOnEnter}
            />
          </RangeInputs>
        );
      }
      case 'in_last':
      case 'not_in_last': {
        const { amount, unit } = parseRelativeDuration(filterCondition.value);
        const unitOptions = RELATIVE_DURATION_UNITS.map((u) => ({
          id: u,
          label: intl.formatMessage(durationUnitLabels[u]),
          value: u,
        }));
        return (
          <RangeInputs>
            <Input
              id={inputId}
              type="number"
              min={1}
              aria-label={intl.formatMessage(messages.specifyValue)}
              placeholder={intl.formatMessage(messages.specifyValue)}
              value={coerceNumberToInputValue(amount)}
              onChange={(e) =>
                onChange({
                  ...filterCondition,
                  value: buildRelativeDuration({
                    amount: parseInt(e.target.value, 10),
                    unit,
                  }),
                })
              }
              onKeyDown={blurOnEnter}
            />
            <Dropdown
              ref={dropdownRef}
              id={`${inputId}-unit`}
              options={unitOptions}
              placeholder={intl.formatMessage(messages.selectUnit)}
              value={unitOptions.find((option) => option.value === unit)}
              onValueChange={(option) =>
                onChange({
                  ...filterCondition,
                  value: buildRelativeDuration({
                    amount,
                    unit: option.value as RelativeDurationUnit,
                  }),
                })
              }
            />
          </RangeInputs>
        );
      }
    }
  }

  if (filterCondition.type === 'enum') {
    switch (filterCondition.operator) {
      case 'eq':
      case 'neq':
      case 'matches':
      case 'not_matches':
        return (
          <Dropdown
            ref={dropdownRef}
            id={inputId}
            options={(schemaProp.alternatives || [])?.map((alternative) => ({
              id: alternative,
              label: alternative,
              value: alternative,
            }))}
            placeholder={intl.formatMessage(messages.specifyValue)}
            onValueChange={(value) =>
              onChange({ ...filterCondition, value: value?.id || '' })
            }
          />
        );
      case 'none_of':
      case 'any_of':
        return (
          <Dropdown
            ref={dropdownRef}
            id={inputId}
            options={(schemaProp.alternatives || [])?.map((alternative) => ({
              id: alternative,
              label: alternative,
              value: alternative,
            }))}
            placeholder={intl.formatMessage(messages.specifyValue)}
            onValueChange={(value) =>
              onChange({
                ...filterCondition,
                value: value?.map((v) => v.id) || [],
              })
            }
            multiSelect
          />
        );
    }
  }

  if (filterCondition.type === 'number') {
    switch (filterCondition.operator) {
      case 'eq':
      case 'neq':
        return (
          <Autocomplete
            {...suggestionProps}
            ref={suggestionRef}
            value={coerceNumberToInputValue(filterCondition.value)}
            onValueChange={(value) =>
              onChange({
                ...filterCondition,
                value: sanitizedParseFloat(value),
              })
            }
          />
        );
      case 'gt':
      case 'gte':
      case 'lt':
      case 'lte':
        return (
          <Input
            id={inputId}
            type="number"
            aria-label={intl.formatMessage(messages.specifyValue)}
            placeholder={intl.formatMessage(messages.specifyValue)}
            value={coerceNumberToInputValue(filterCondition.value)}
            onChange={(e) =>
              onChange({
                ...filterCondition,
                value: parseFloat(e.target.value),
              })
            }
            onKeyDown={blurOnEnter}
          />
        );
      case 'between':
      case 'not_between': {
        return (
          <RangeInputs>
            <Input
              id={`${inputId}-from`}
              type="number"
              aria-label={intl.formatMessage(messages.fromValue)}
              value={coerceNumberToInputValue(filterCondition.value[0])}
              onChange={(e) =>
                onChange({
                  ...filterCondition,
                  value: [parseFloat(e.target.value), filterCondition.value[1]],
                })
              }
              onKeyDown={blurOnEnter}
            />
            <RangeSeparator />
            <Input
              id={`${inputId}-to`}
              type="number"
              aria-label={intl.formatMessage(messages.toValue)}
              value={coerceNumberToInputValue(filterCondition.value[1])}
              onChange={(e) =>
                onChange({
                  ...filterCondition,
                  value: [filterCondition.value[0], parseFloat(e.target.value)],
                })
              }
              onKeyDown={blurOnEnter}
            />
          </RangeInputs>
        );
      }
      case 'any_of':
      case 'none_of':
        return (
          <MultiTextField
            {...suggestionProps}
            type="number"
            value={filterCondition.value}
            onChange={(value: number[]) =>
              onChange({ ...filterCondition, value })
            }
          />
        );
    }
  }

  if (filterCondition.type === 'string') {
    switch (filterCondition.operator) {
      case 'eq':
      case 'neq':
        return (
          <Autocomplete
            {...suggestionProps}
            ref={suggestionRef}
            value={filterCondition.value || ''}
            onValueChange={(value) => onChange({ ...filterCondition, value })}
          />
        );
      case 'matches':
      case 'not_matches':
        return (
          <Autocomplete
            {...suggestionProps}
            ref={suggestionRef}
            freeSolo
            value={filterCondition.value || ''}
            onValueChange={(value) => onChange({ ...filterCondition, value })}
          />
        );
      case 'any_of':
      case 'none_of':
        return (
          <MultiTextField
            {...suggestionProps}
            type="string"
            value={filterCondition.value}
            onChange={(value: string[]) =>
              onChange({ ...filterCondition, value })
            }
          />
        );
    }
  }

  if (filterCondition.type === 'array:number') {
    switch (filterCondition.operator) {
      case 'none_of':
      case 'any_of':
      case 'all_of':
        return (
          <MultiTextField
            {...suggestionProps}
            type="number"
            value={filterCondition.value}
            onChange={(value: number[]) =>
              onChange({ ...filterCondition, value })
            }
          />
        );
    }
  }

  if (filterCondition.type === 'array:string') {
    switch (filterCondition.operator) {
      case 'none_of':
      case 'any_of':
      case 'all_of':
        return (
          <MultiTextField
            {...suggestionProps}
            type="string"
            value={filterCondition.value}
            onChange={(value: string[]) =>
              onChange({ ...filterCondition, value })
            }
          />
        );
    }
  }
  return null;
});
FilterConditionInput.displayName = 'FilterConditionInput';

export default FilterConditionInput;
